import { Suspense, useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
   matchPath,
   Outlet,
   useLocation,
   useNavigate,
   useParams,
} from "react-router"
import Controls from "./Controls"
import LogPanel from "../../components/LogPanel"
import Loader from "@/components/Loader"
import Grid from "@/components/shared/Grid"
import GridItem from "@/components/shared/Grid/GridItem"
import { SpinnerSize } from "@/components/shared/Spinner"
import Tabs from "@/components/shared/Tabs"
import { InmateDetailOverviewRoutes } from "@/constants/routes"
import { useUserRole } from "@/hooks/useUserRole"
import { AppDispatch, RootState } from "@/store"
import { selectTimezone } from "@/store/facilitiesSlice"
import { fetchInmate } from "@/store/inmatesSlice"
import { fetchInmateLogs } from "@/store/logsSlice"
import { LogSource } from "@/types/logs"
import { Feature } from "@/types/user"
import { formatInmateName } from "@/utils/index"
import "./index.scss"

const InmateDetailOverview = () => {
   const dispatch = useDispatch<AppDispatch>()
   const navigate = useNavigate()
   const location = useLocation()
   const { id } = useParams<{ id: string }>()
   const { inmates, currentInmate, inmateVitals, loadingInmate } = useSelector(
      ({ inmatesReducer }: RootState) => inmatesReducer
   )

   const { inmateLogs, loadingInmateLogs, errorInmateLogs } = useSelector(
      ({ logsReducer }: RootState) => logsReducer
   )
   const canAccessExport = useUserRole(Feature.EXPORT)

   const [isInmateLogExpanded, setIsInmateLogExpanded] = useState(false)

   useEffect(() => {
      async function fetchInmateData() {
         if (!id) return
         try {
            await dispatch(fetchInmate(id)).unwrap()
         } catch (error) {
            console.error("Error fetching inmate data:", error)
         }
      }
      fetchInmateData()
   }, [dispatch, id])

   const timezone = useSelector(selectTimezone)

   const getLogData = useCallback(
      (selectedDate: string) => {
         if (!id || !timezone) return
         try {
            dispatch(
               fetchInmateLogs({
                  inmateId: id,
                  timezone,
                  startDate: selectedDate,
                  endDate: selectedDate,
               })
            ).unwrap()
         } catch (err) {
            console.error("Log data fetch failed:", err)
         }
      },
      [dispatch, id, timezone]
   )

   const inmate = inmates.find((inmate) => inmate.id === id) ?? currentInmate
   const inmateTitle = inmate?.customIdentifier
      ? `${inmate.customIdentifier}`
      : formatInmateName(inmate ?? null)

   const tabItems = useMemo(
      () => [
         {
            id: "summary",
            label: "Summary",
            value: "summary",
            route: InmateDetailOverviewRoutes.SUMMARY,
         },
         ...(canAccessExport
            ? [
                 {
                    id: "export",
                    label: "Export",
                    value: "export",
                    route: InmateDetailOverviewRoutes.EXPORT,
                 },
              ]
            : []),
      ],
      [canAccessExport]
   )

   const activeTabId = useMemo(() => {
      const currentRoute = location.pathname
      const activeTab = tabItems.find((tab) =>
         matchPath({ path: tab.route, end: false }, currentRoute)
      )
      return activeTab ? activeTab.id : tabItems[0].id
   }, [location.pathname, tabItems])

   const handleTabChange = useCallback(
      (tabId: string) => {
         const tab = tabItems.find((t) => t.id === tabId)
         if (tab?.route && id && !id?.includes(":id")) {
            const routeWithAlertId = tab.route.replace(":id", id)
            navigate(routeWithAlertId)
         }
      },
      [id, navigate, tabItems]
   )

   return (
      <Grid>
         <GridItem size={10}>
            <Grid
               direction="column"
               flexWrap="nowrap"
               className="inmate-detail-overview"
            >
               <Suspense fallback={<Loader />}>
                  {loadingInmate ? (
                     <Loader size={SpinnerSize.LARGE} />
                  ) : (
                     <>
                        <GridItem size={12}>
                           <Controls
                              wearableId={inmate?.sensor?.macAddress ?? ""}
                              //inmateId={inmate?.id ?? ""}
                              title={inmateTitle}
                              chargeLevel={inmateVitals?.batteryPercent ?? 0}
                              bookingNumber={inmate?.bookingNumber}
                              inmateNumber={inmate?.inmateNumber}
                              housingBlock={inmate?.housingBlock}
                              housingCell={inmate?.housingCell}
                              housingUnit={inmate?.housingUnit}
                           />
                        </GridItem>
                        <GridItem size={12}>
                           <Tabs
                              buttonsClassName="inmate-detail-overview__tabs"
                              activeTabId={activeTabId}
                              tabs={tabItems}
                              onTabChange={handleTabChange}
                           />
                        </GridItem>
                        <GridItem size={12}>
                           <Outlet />
                        </GridItem>
                     </>
                  )}
               </Suspense>
            </Grid>
         </GridItem>
         <GridItem
            size={2}
            className="inmate-detail-overview__sidebar"
         >
            {timezone && (
               <LogPanel
                  title="Inmate Log"
                  timezone={timezone}
                  source={LogSource.INMATE}
                  getData={getLogData}
                  logData={inmateLogs}
                  loading={loadingInmateLogs}
                  error={errorInmateLogs}
                  isExpanded={isInmateLogExpanded}
                  name={inmateTitle}
                  onClose={() => setIsInmateLogExpanded(false)}
                  onExpand={() => setIsInmateLogExpanded(true)}
               />
            )}
         </GridItem>
      </Grid>
   )
}

export default InmateDetailOverview
